/**
 * The one place the React app talks to the backend.
 *
 * The API is unchanged by this migration -- `app/src/routes.js` is the same
 * file it was before. This wrapper exists so the token, the JSON handling and
 * the error shape are identical everywhere, exactly as the vanilla client's
 * `api()` was, rather than being re-derived in each screen.
 *
 * Errors carry `.status`, because the screens genuinely branch on it: 429 is
 * the rate limiter and says something different to a respondent than 401.
 *
 * `auth` overrides the current token for one call. Only the survey's
 * last-chance save uses it: that save belongs to the session it was made in,
 * and by the time it is sent a sign-out may already have cleared the token.
 */

let token = null;

export const setToken = (value) => { token = value; };
export const getToken = () => token;

export async function api(path, { method = 'GET', body, auth = token } = {}) {
  const response = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(auth ? { Authorization: `Bearer ${auth}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(new Error(payload.error ?? 'Request failed'), { status: response.status });
  }
  return payload;
}
